import { setValueValue } from '@databases/collab/y-database';
import { type MutableDatabase } from '@databases/model/database';
import { FieldType } from '@databases/model/field';
import {
  type DatabaseFieldType,
  DEFAULT_ENTRY_ID,
  type DatabaseEntry,
  type DatabaseField,
} from '@databases/types/database';
import {
  type DatabaseCardWidth,
  type DatabaseFilter,
  DatabaseLayoutType,
  type DatabaseView,
} from '@databases/types/database/view';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { DatabaseChangeType } from '@databases/types/redux';
import { type DatabaseChange } from '@databases/utils/database/changesets';
import { pickNewColor } from '@databases/utils/database/field-types/select';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { valueToCollabJSON } from '@databases/utils/redux/collab/convert-to-collab';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { fieldToDatabase } from '@databases/utils/redux/collab/convert-to-database';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { addChangeToYjs } from '@databases/utils/redux/redux-to-yjs/add-change-to-yjs';
import { generateId } from '@databases/utils/utils';

export interface AgentLayoutSettingsOptions {
  layoutType?: DatabaseLayoutType;
  groupFieldId?: string;
  numberedEntries?: boolean;
  cardWidth?: DatabaseCardWidth;
  showFieldNames?: boolean;
}

export class DatabaseAgent {
  private database: MutableDatabase;

  constructor(database: MutableDatabase) {
    this.database = database;
  }

  public addField(field: DatabaseField, index?: number): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        index,
      },
      undefined
    );
  }

  /**
   * Renames a field
   * @param fieldName - The name of the field to set the name for.
   * @param newName - The new name for the field.
   * @throws Error if the field is not found.
   */
  public setFieldName(fieldName: string, newName: string): void {
    const fieldId = this.getFieldIdByName(fieldName);

    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_NAME,
        fieldId,
        name: newName,
        delta: [
          { delete: fieldName.length },
          {
            insert: newName,
          },
        ],
      },
      undefined
    );
  }

  /**
   * Changes the type of a field
   * @param fieldName - The name of the field to set the type for.
   * @param type The new type for the field.
   * @throws Error if the field is not found.
   */
  public setFieldType(fieldName: string, type: DatabaseFieldType): void {
    const fieldId = this.getFieldIdByName(fieldName);

    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_TYPE,
        fieldId,
        fieldType: type,
      },
      undefined
    );
  }

  /**
   * Sets whether a field allows multiple values.
   * @param fieldName - The name of the field to set the type for.
   * @param multiple Allow multiple values for this field.
   * @throws Error if the field is not found.
   */
  public setFieldMultiple(fieldName: string, multiple: boolean): void {
    const fieldId = this.getFieldIdByName(fieldName);

    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_IS_MULTI,
        fieldId,
        isMulti: multiple,
      },
      undefined
    );
  }

  /**
   * Sets the description of a field
   * @param fieldName - The name of the field to set the description for.
   * @param description - The new description for the field.
   * @throws Error if the field is not found.
   */
  public setFieldDescription(fieldName: string, description: string): void {
    const fieldId = this.getFieldIdByName(fieldName);
    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    const field = this.database.fieldsById[fieldId];

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION,
        fieldId,
        showDescription: true,
      },
      undefined
    );

    const currentDescription = field.description;
    const delta =
      currentDescription === undefined || currentDescription === ''
        ? [{ insert: description }]
        : [{ delete: currentDescription.length }, { insert: description }];

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_DESCRIPTION,
        fieldId,
        description,
        delta,
      },
      undefined
    );
  }

  public removeField(fieldId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.DELETE_FIELD,
        fieldId,
      },
      undefined
    );
  }

  public getCloudId(fieldId: string): string | undefined {
    return this.database.fieldsById[fieldId].cloudId;
  }

  public setCloudId(fieldId: string, cloudId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_CLOUD_ID,
        fieldId,
        cloudId,
      },
      undefined
    );
  }

  public addEntry(entry: DatabaseEntry): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.ADD_ENTRY,
        entry,
      },
      undefined
    );
  }

  public removeEntry(entryId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.DELETE_ENTRY,
        entryId,
      },
      undefined
    );
  }

  public addView(view: DatabaseView): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.ADD_SAVED_VIEW,
        view,
      },
      undefined
    );
  }

  public deleteView(viewId: string): void {
    this.database.mutate((db) => {
      db.deleteView(viewId);
    });
  }

  public isViewsEmpty(): boolean {
    return this.database.views.length === 0;
  }

  public getViewCount(): number {
    return this.database.views.length;
  }

  public setDefaultView(viewId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_DEFAULT_SAVED_VIEW,
        viewId,
      },
      undefined
    );
  }

  public updateViewLayout(viewId: string, settings: AgentLayoutSettingsOptions): void {
    const changes: DatabaseChange[] = [];
    if (settings.layoutType) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE,
        viewId,
        newLayoutType: settings.layoutType,
      });
    }
    if (settings.groupFieldId) {
      changes.push({
        type: DatabaseChangeType.SET_GROUPING_FIELD,
        viewId,
        fieldId: settings.groupFieldId,
      });
    }
    if (settings.numberedEntries !== undefined) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES,
        viewId,
        newShowNumberedEntries: settings.numberedEntries,
      });
    }
    if (settings.cardWidth) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH,
        viewId,
        newCardWidth: settings.cardWidth,
      });
    }
    if (settings.showFieldNames !== undefined) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES,
        viewId,
        newShowFieldNames: settings.showFieldNames,
      });
    }
    addChangeToYjs(
      this.database,
      { type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES, changes },
      undefined
    );
  }

  public addSortToView(viewId: string, fieldId: string, ascending: boolean): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.APPEND_SAVED_VIEW_SORT,
            viewId,
            sort: {
              fieldId,
              ascending,
            },
          },
        ],
      },
      undefined
    );
  }

  public updateSort(viewId: string, fieldId: string, ascending: boolean): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE,
            viewId,
            fieldId,
            ascending,
          },
        ],
      },
      undefined
    );
  }

  public removeSort(viewId: string, fieldId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.DELETE_SAVED_VIEW_SORT,
            viewId,
            fieldId,
          },
        ],
      },
      undefined
    );
  }

  public hasSortForField(viewId: string, fieldId: string): boolean {
    const view = this.database.viewsById[viewId];
    if (!view) {
      return false;
    }
    return view.sorts.toArray().some((sort) => sort.fieldId === fieldId);
  }

  public setViewFilter(viewId: string, filter: DatabaseFilter): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.SET_SAVED_VIEW_FILTER,
            viewId,
            newFilter: filter,
            path: [],
          },
        ],
      },
      undefined
    );
  }

  public clearViewFilter(viewId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.CLEAR_SAVED_VIEW_FILTER,
            viewId,
          },
        ],
      },
      undefined
    );
  }

  public getViewIdByIndex(index: number): string | undefined {
    return this.database.views[index];
  }

  public getViewIndexById(viewId: string): number {
    return this.database.views.indexOf(viewId);
  }

  public getViewIdByName(name: string): string | undefined {
    for (const [viewId, view] of Object.entries(this.database.viewsById)) {
      if (view.name === name) {
        return viewId;
      }
    }
  }

  public getViewById(viewId: string) {
    return this.database.viewsById[viewId];
  }

  public getViewLayoutType(viewId: string): DatabaseLayoutType | undefined {
    const view = this.database.viewsById[viewId];
    if (!view) {
      return undefined;
    }
    const layoutType = view.layoutType;
    if (layoutType === 'table') {
      return DatabaseLayoutType.TABLE;
    }
    if (layoutType === 'board') {
      return DatabaseLayoutType.BOARD;
    }
    if (layoutType === 'gallery') {
      return DatabaseLayoutType.GALLERY;
    }
    if (layoutType === 'calendar') {
      return DatabaseLayoutType.CALENDAR;
    }
    return undefined;
  }

  public updateViewName(viewName: string, newName: string): void {
    const viewId = this.getViewIdByName(viewName);

    if (!viewId) {
      throw new Error(`View with name ${viewName} not found`);
    }

    // Check if the new name already exists
    const existingViewId = this.getViewIdByName(newName);
    if (existingViewId) {
      throw new Error(`View with name ${newName} already exists`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_NAME,
        viewId,
        newName,
        delta: [
          { delete: viewName.length },
          {
            insert: newName,
          },
        ],
      },
      undefined
    );
  }

  public reorderView(viewId: string, newIndex: number): void {
    const currentIndex = this.getViewIndexById(viewId);
    const adjustedIndex = newIndex > currentIndex ? newIndex + 1 : newIndex;

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.REORDER_SAVED_VIEW,
        viewId,
        newIndex: adjustedIndex,
      },
      undefined
    );
  }

  public getFieldIdByName(name: string): string | undefined {
    return this.database.fields.find((fieldId) => {
      const field = this.database.fieldsById[fieldId];
      return field?.name === name;
    });
  }

  public getFieldById(fieldId: string): DatabaseField | undefined {
    const field = this.database.fieldsById[fieldId];
    if (!field) {
      return undefined;
    }

    return fieldToDatabase(fieldId, field.toJSON());
  }

  public setValue(
    entryId: string,
    fieldId: string,
    value: any,
    resetValue = false
  ): void {
    this.database.mutate((db) => {
      const entry =
        entryId === DEFAULT_ENTRY_ID ? db.defaultEntry : db.entriesById[entryId];
      if (!entry) {
        throw new Error(`Entry with ID ${entryId} not found`);
      }

      const field = db.fieldsById[fieldId];
      if (!field) {
        throw new Error(`Field with ID ${fieldId} not found`);
      }

      if (resetValue) {
        entry.removeValue(fieldId);
      }

      const mutableValue = entry.setValue(fieldId, field.type);

      // TODO: To support attachment and pageLink we might have to think about the referenceContentIdMap
      const valueJson = valueToCollabJSON(value, {});

      if (valueJson) {
        setValueValue(mutableValue, valueJson);
      }
    });
  }

  public getOrCreateSelectOption(fieldId: string, optionValue: string): string {
    const field = this.database.fieldsById[fieldId];
    if (!field) {
      throw new Error(`Field with ID ${fieldId} not found`);
    }

    if (field.type !== FieldType.Select) {
      throw new Error(`Field ${fieldId} is not a select field`);
    }

    const existingOption = field.selectOptions.options.find(
      (option) => option.label === optionValue
    );

    if (existingOption) {
      return existingOption.id;
    }

    const color = pickNewColor(field.selectOptions.options);

    const optionId = generateId();
    field.selectOptions.add({
      id: optionId,
      label: optionValue,
      color,
    });

    return optionId;
  }

  public clearDatabase(): void {
    this.database.mutate((db) => {
      const entryIds = [...db.entries];
      const fieldIds = [...db.fields];
      const viewIds = [...db.views];

      entryIds.forEach((entryId) => {
        db.deleteEntry(entryId);
      });

      fieldIds.forEach((fieldId) => {
        db.deleteField(fieldId);
      });

      viewIds.forEach((viewId) => {
        db.deleteView(viewId);
      });
    });
  }
}
